import { useCallback, useEffect, useState } from "react";

import { themeVar as colors } from "../design-tokens";
import { AppShell } from "../ui/AppShell";
import { PageHead } from "../ui/PageHead";
import { blockStyle } from "../ui/Block";
import { useDocumentTitle, vbTitle } from "../ui/useDocumentTitle";
import {
  MANAGER_ACTIONS, NOT_OFFERED_LINE, addMember, errorLine, formatWhen, isForbidden, isNotOffered,
  listMembers, removeMember, transition, type MlsMember, type MlsState,
} from "./api";
import { fieldStyle, ghostButton, primaryButton, whenDisabled } from "./styles";

/**
 * `/mls-access/admin` — the NTREIS group, for its two managers (Release 1.1 Slice A).
 *
 * Only an NTREIS group manager may add, remove, pause, resume, revoke or reopen anyone. Platform
 * admin alone is not enough, and the SERVER is the control on every request: a person who is not
 * a manager and types the URL gets one calm line, not data. Every change needs a reason, which the
 * server also enforces and writes to the audit trail and the usage ledger. Each action offered is
 * one the server's table allows from that state, so no button is ever a guaranteed 409.
 */
type Load =
  | { kind: "loading" }
  | { kind: "off" }
  | { kind: "forbidden" }
  | { kind: "error"; message: string }
  | { kind: "ready"; rows: MlsMember[] };

export const MANAGERS_ONLY_LINE = "This page is for the NTREIS group managers.";

export function MlsAdminScreen() {
  useDocumentTitle(vbTitle("NTREIS group"));
  const [load, setLoad] = useState<Load>({ kind: "loading" });

  const refresh = useCallback(async () => {
    try {
      setLoad({ kind: "ready", rows: await listMembers() });
    } catch (e) {
      if (isNotOffered(e)) setLoad({ kind: "off" });
      else if (isForbidden(e)) setLoad({ kind: "forbidden" });
      else setLoad({ kind: "error", message: errorLine(e, "We couldn't load the group just now.") });
    }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  const open = load.kind === "ready" ? load.rows.filter((r) => r.open) : [];
  const past = load.kind === "ready" ? load.rows.filter((r) => !r.open) : [];
  return (
    <AppShell step="mlsadmin" content="form">
      <div style={{ ...blockStyle("form"), padding: "8px 0 48px" }}>
        <PageHead title="NTREIS group"
                  sub="Who may use MLS data. NTREIS bills for each agent who connects or uses the feed. Every change needs a reason, and the member can see it." />
        {load.kind === "loading" && <p style={{ color: colors.textSecondary }}>Loading…</p>}
        {load.kind === "off" && <p>{NOT_OFFERED_LINE}</p>}
        {load.kind === "forbidden" && <p>{MANAGERS_ONLY_LINE}</p>}
        {load.kind === "error" && <p role="status">{load.message}</p>}
        {load.kind === "ready" && (
          <>
            <AddMember onChanged={refresh} />
            {open.length === 0 && <p>No one is in the NTREIS group yet.</p>}
            {open.map((r) => <MemberRow key={r.user_id} row={r} onChanged={refresh} />)}
            {past.length > 0 && (
              <details style={{ marginTop: 20 }}>
                <summary style={{ cursor: "pointer" }}>Past members ({past.length})</summary>
                <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 14, lineHeight: 1.6 }}>
                  {past.map((r, i) => (
                    <li key={`${r.user_id}-${i}`}>{r.name} ({r.email}): added {formatWhen(r.added_at)},
                      removed {formatWhen(r.removed_at)}{r.removed_reason ? ` — ${r.removed_reason}` : ""}</li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function AddMember({ onChanged }: { onChanged: () => void }) {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!email.trim()) { setError("Enter the person's ValuBrief login email."); return; }
    if (!reason.trim()) { setError("Enter a reason for this change."); return; }
    setBusy(true); setError(null);
    try { await addMember(email.trim(), reason.trim()); setEmail(""); setReason(""); onChanged(); }
    catch (e) { setError(errorLine(e, "We couldn't add that person just now.")); }
    finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} aria-label="Add a member" data-testid="mls-add"
          style={{ border: `1px solid ${colors.border}`, borderRadius: 10, padding: 16, marginTop: 12 }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600 }} htmlFor="mls-add-email">
        Login email
      </label>
      <input id="mls-add-email" value={email} onChange={(e) => setEmail(e.target.value)}
             autoComplete="off" style={{ ...fieldStyle, maxWidth: 420 }} />
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginTop: 8 }} htmlFor="mls-add-reason">
        Reason (required)
      </label>
      <input id="mls-add-reason" value={reason} onChange={(e) => setReason(e.target.value)}
             style={{ ...fieldStyle, maxWidth: 420 }} />
      <p style={{ margin: "6px 0 8px", fontSize: 13, color: colors.textSecondary }}>
        Adding someone does not turn MLS on. They turn it on themselves by confirming their NTREIS
        member ID and the data-use terms on their own card.
      </p>
      {error && <p role="alert" style={{ margin: "0 0 8px", fontSize: 14 }}>{error}</p>}
      <button type="submit" className="vb-btn-primary" disabled={busy}
              style={{ ...primaryButton, ...whenDisabled(busy) }}>Add to the NTREIS group</button>
    </form>
  );
}

function MemberRow({ row, onChanged }: { row: MlsMember; onChanged: () => void }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const actions = row.state ? MANAGER_ACTIONS[row.state] : [];

  async function run(fn: () => Promise<unknown>) {
    if (!reason.trim()) { setError("Enter a reason for this change."); return; }
    setBusy(true); setError(null);
    try { await fn(); setReason(""); onChanged(); }
    catch (e) { setError(errorLine(e, "We couldn't make that change just now.")); }
    finally { setBusy(false); }
  }
  const act = (to: MlsState) => run(() => transition(row.user_id, to, reason.trim()));

  return (
    <article data-testid={`mls-member-${row.user_id}`}
             style={{ border: `1px solid ${colors.border}`, borderRadius: 10, padding: 16, marginTop: 12 }}>
      <p style={{ margin: 0, fontWeight: 600 }}>{row.name} <span style={{ fontWeight: 400, color: colors.textSecondary }}>{row.email}</span>
        {row.manager && <span style={{ fontWeight: 400, color: colors.textSecondary }}> · group manager</span>}</p>
      <p style={{ margin: "4px 0", fontSize: 14 }}>
        <span data-testid="mls-member-state"><strong>{row.state ?? "None"}</strong></span>
        {" · "}NTREIS member ID {row.mls_member_id ?? "not confirmed yet"}
        {" · "}added {formatWhen(row.added_at) ?? "date not recorded"}
      </p>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600 }} htmlFor={`reason-${row.user_id}`}>
        Reason (required)
      </label>
      <input id={`reason-${row.user_id}`} value={reason} onChange={(e) => setReason(e.target.value)}
             style={{ ...fieldStyle, maxWidth: 420 }} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
        {actions.map((a) => (
          <button key={a.to} type="button" className={a.to === "Revoked" ? "vb-btn" : "vb-btn-primary"}
                  disabled={busy} onClick={() => act(a.to)}
                  style={{ ...(a.to === "Revoked" ? ghostButton : primaryButton), ...whenDisabled(busy) }}>
            {a.label}
          </button>
        ))}
        <button type="button" className="vb-btn" disabled={busy}
                onClick={() => run(() => removeMember(row.user_id, reason.trim()))}
                style={{ ...ghostButton, ...whenDisabled(busy) }}>
          Remove from group
        </button>
      </div>
      {error && <p role="alert" style={{ margin: "8px 0 0", fontSize: 14 }}>{error}</p>}
    </article>
  );
}
